import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/shadcn/components/ui/button"
import { Label } from "@/shadcn/components/ui/label"
import { Spinner } from "@/shadcn/components/ui/spinner"
import { Input } from "@/shared/components/Input"
import { createManagedDialog } from "@/shared/lib/dialog"
import { toast } from "@/shared/lib/toast"
import { fetchMockServerProjectRequests } from "../model/api"
import { useMockServerStore } from "../model/store"
import { MockRequestListItem } from "./MockRequestListItem"

export const MockServerConfigDialog = createManagedDialog(() => {
  const { t } = useTranslation("mock-server")

  const apiKey = useMockServerStore((state) => state.apiKey)
  const requests = useMockServerStore((state) => state.requests)

  const setApiKey = useMockServerStore((state) => state.setApiKey)
  const mergeRequests = useMockServerStore((state) => state.mergeRequests)
  const setRequestEnabled = useMockServerStore((state) => state.setRequestEnabled)
  const setResponseTag = useMockServerStore((state) => state.setResponseTag)
  const setAllEnabled = useMockServerStore((state) => state.setAllEnabled)

  const [isLoading, setIsLoading] = useState(false)
  const [apiKeyDraft, setApiKeyDraft] = useState(apiKey)

  const requestList = Object.values(requests)

  async function loadRequests(key: string, persistKey: boolean) {
    setIsLoading(true)

    try {
      const serverRequests = await fetchMockServerProjectRequests(key)
      if (persistKey) {
        setApiKey(key)
      }
      mergeRequests(serverRequests)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t("loadError")

      toast({
        type: "error",
        message: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!apiKey) {
      return
    }

    void loadRequests(apiKey, false)
    // Fetch once when the dialog opens with a stored key.
  }, [])

  function renderKeyForm() {
    return (
      <form
        id="mock-server-api-key-form"
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          const trimmed = apiKeyDraft.trim()
          if (!trimmed) {
            toast({ type: "error", message: t("apiKeyRequired") })
            return
          }
          void loadRequests(trimmed, true)
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="mock-server-api-key">{t("apiKeyLabel")}</Label>
          <Input
            id="mock-server-api-key"
            value={apiKeyDraft}
            onChange={(event) => setApiKeyDraft(event.target.value)}
            placeholder={t("apiKeyPlaceholder")}
            autoComplete="off"
            type="password"
          />
        </div>
      </form>
    )
  }

  function renderLoading() {
    return (
      <div className="align-center flex h-25 items-center justify-center">
        <Spinner color="primary" size={40} thickness={2} />
      </div>
    )
  }

  function renderEmptyList() {
    return <p className="text-sm text-muted-foreground">{t("emptyList")}</p>
  }

  function renderRequestList() {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setAllEnabled(true)}>
            {t("enableAll")}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setAllEnabled(false)}>
            {t("disableAll")}
          </Button>
        </div>
        <ul className="max-h-[50vh] overflow-x-hidden overflow-y-auto px-2">
          {requestList.map((request) => (
            <MockRequestListItem
              key={request.id}
              request={request}
              onEnabledChange={(enabled) => setRequestEnabled(request.id, enabled)}
              onResponseTagChange={(responseTag) => setResponseTag(request.id, responseTag)}
            />
          ))}
        </ul>
      </div>
    )
  }

  return {
    title: t("title"),
    contentClassName: "sm:max-w-2xl",
    bodyClassName: "overflow-x-hidden px-1 py-1",
    renderBody: () => {
      if (!apiKey) {
        return renderKeyForm()
      }

      if (isLoading) {
        return renderLoading()
      }

      if (requestList.length === 0) {
        return renderEmptyList()
      }

      return renderRequestList()
    },
    renderFooter: !apiKey
      ? () => (
          <Button
            type="submit"
            form="mock-server-api-key-form"
            loading={isLoading}
            disabled={isLoading}
          >
            {t("continue")}
          </Button>
        )
      : undefined,
  }
})
